// src/components/maintenance/AdminNoticeboard.jsx
//
// The authoring screen for the noticeboard, in the admin panel (Noticeboard
// contract v1.1 §3–§4). Admins are the only issuers; the backend is the single
// source of truth, and no public client can create or change a notice.
//
//   GET    /api/admin/noticeboard        all notices: drafts, live, expired, withdrawn
//   POST   /api/admin/noticeboard        create
//   PATCH  /api/admin/noticeboard/<id>   edit; {"material": true} bumps the revision
//   DELETE /api/admin/noticeboard/<id>   withdraw (soft delete)
//
// Times are typed in the admin's own zone and sent as UTC instants.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../../config/apiConfig';
import { authHeaders, clearAdminToken } from './adminAuth';

const REASONS = {
  heading_invalid: 'The heading must be 1 to 80 characters.',
  body_invalid: 'The body must be 1 to 600 characters.',
  kind_invalid: 'Pick a kind.',
  date_invalid: 'The date must be a calendar date.',
  window_invalid: 'The impact window needs both ends, and the end must be after the start.',
  surfaces_invalid: 'Pick at least one surface.',
  publish_invalid: 'The publish times are not valid.',
  notice_not_found: 'That notice no longer exists.',
};

const pad = (n) => String(n).padStart(2, '0');
// ISO instant → the value a datetime-local input wants, in this device's zone.
const toLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
// datetime-local value → ISO instant with Z, or null when empty.
const toIso = (local) => {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().replace(/\.\d{3}Z$/, 'Z');
};
const today = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const zoneName = () => {
  try {
    const p = new Intl.DateTimeFormat('en-GB', { timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return (p.find((x) => x.type === 'timeZoneName') || {}).value || 'local time';
  } catch { return 'local time'; }
};
const short = (iso) => { const d = new Date(iso); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(); };

const statusOf = (n, now = Date.now()) => {
  if (n.withdrawn_at) return 'withdrawn';
  if (n.publish_from && new Date(n.publish_from).getTime() > now) return 'scheduled';
  if (n.publish_to && new Date(n.publish_to).getTime() <= now) return 'expired';
  return 'live';
};

const BLANK = { id: null, kind: 'maintenance', date: today(), heading: '', body: '', from: '', to: '', suite: true, kuestion: true, publishFrom: '', publishTo: '', material: false };

const formOf = (n) => ({
  id: n.id, kind: n.kind || 'info', date: n.date || today(), heading: n.heading || '', body: n.body || '',
  from: toLocal(n.affects_from), to: toLocal(n.affects_to),
  suite: (n.surfaces || []).includes('suite'), kuestion: (n.surfaces || []).includes('kuestion'),
  publishFrom: toLocal(n.publish_from), publishTo: toLocal(n.publish_to), material: false,
});

export default function AdminNoticeboard({ onClose, onLogout }) {
  const [notices, setNotices] = useState(null);
  const [form, setForm] = useState(null); // null = list; object = editing or creating
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const zone = useMemo(zoneName, []);

  const call = useCallback(async (url, method, body) => {
    const res = await fetch(url, {
      method,
      headers: { ...(body ? { 'Content-Type': 'application/json' } : {}), ...authHeaders() },
      body: body ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
    let json = null;
    try { json = await res.json(); } catch { /* no body */ }
    if (res.status === 401) { clearAdminToken(); if (onLogout) onLogout(); }
    return { ok: res.ok && json?.status === 'success', status: res.status, json };
  }, [onLogout]);

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await call(API_ENDPOINTS.ADMIN_NOTICEBOARD, 'GET');
      if (r.ok) setNotices(Array.isArray(r.json.notices) ? r.json.notices : []);
      else if (r.status !== 401) { setNotices([]); setError(REASONS[r.json?.reason] || r.json?.reason || 'Could not read the noticeboard.'); }
    } catch { setNotices([]); setError('Server unreachable.'); }
  }, [call]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (form) setForm(null); else onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [form, onClose]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // The same rules the server enforces, so a mistake is named before the round trip.
  const problem = form ? (
    !form.heading.trim() || form.heading.trim().length > 80 ? REASONS.heading_invalid
      : !form.body.trim() || form.body.length > 600 ? REASONS.body_invalid
        : !form.date ? REASONS.date_invalid
          : (Boolean(form.from) !== Boolean(form.to)) || (form.from && form.to && new Date(form.to) <= new Date(form.from)) ? REASONS.window_invalid
            : !form.suite && !form.kuestion ? REASONS.surfaces_invalid
              : form.publishFrom && form.publishTo && new Date(form.publishTo) <= new Date(form.publishFrom) ? REASONS.publish_invalid
                : ''
  ) : '';

  const save = async () => {
    if (!form || problem || busy) return;
    setBusy(true); setError('');
    const editing = Boolean(form.id);
    const body = {
      kind: form.kind, date: form.date, heading: form.heading.trim(), body: form.body.trim(),
      surfaces: [form.suite && 'suite', form.kuestion && 'kuestion'].filter(Boolean),
    };
    if (form.from && form.to) { body.affects_from = toIso(form.from); body.affects_to = toIso(form.to); }
    else if (editing) { body.affects_from = null; body.affects_to = null; } // clearing a window on an edit
    if (form.publishFrom) body.publish_from = toIso(form.publishFrom);
    if (form.publishTo) body.publish_to = toIso(form.publishTo);
    else if (editing) body.publish_to = null;
    if (editing && form.material) body.material = true;
    try {
      const r = editing
        ? await call(API_ENDPOINTS.ADMIN_NOTICE(form.id), 'PATCH', body)
        : await call(API_ENDPOINTS.ADMIN_NOTICEBOARD, 'POST', body);
      if (r.ok) { setForm(null); await load(); }
      else if (r.status !== 401) setError(REASONS[r.json?.reason] || r.json?.reason || 'The notice was not saved.');
    } catch { setError('Server unreachable.'); } finally { setBusy(false); }
  };

  const withdraw = async (n) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Withdraw "${n.heading}"? Readers stop seeing it within a minute. It is kept for audit and its id is never reused.`)) return;
    setBusy(true); setError('');
    try {
      const r = await call(API_ENDPOINTS.ADMIN_NOTICE(n.id), 'DELETE');
      if (r.ok) await load();
      else if (r.status !== 401) setError(REASONS[r.json?.reason] || r.json?.reason || 'The notice was not withdrawn.');
    } catch { setError('Server unreachable.'); } finally { setBusy(false); }
  };

  return (
    <div className="admin-nb-overlay" role="dialog" aria-label="Noticeboard authoring">
      <div className="admin-nb">
        <div className="admin-nb-head">
          <div>
            <strong>Noticeboard</strong>
            <span>{form ? (form.id ? 'Edit a notice' : 'New notice') : 'Advisory notices on every header. They never block anything.'}</span>
          </div>
          <button type="button" className="admin-toggle-x" onClick={() => (form ? setForm(null) : onClose())} aria-label={form ? 'Back to the list' : 'Close'}>×</button>
        </div>

        {error && <div className="admin-toggle-error" role="alert">{error}</div>}

        {!form && (
          <>
            <div className="admin-nb-bar">
              <button type="button" className="admin-nb-primary" onClick={() => { setError(''); setForm({ ...BLANK, date: today() }); }}>New notice</button>
              <span className="admin-nb-muted">{notices ? `${notices.length} in total · ${notices.filter((n) => statusOf(n) === 'live').length} live` : ''}</span>
            </div>
            {notices === null && <div className="admin-surface-loading">Reading notices…</div>}
            {notices && notices.length === 0 && <div className="admin-surface-loading">No notices yet. The board is empty on every surface until you create one.</div>}
            {notices && notices.length > 0 && (
              <ul className="admin-nb-list">
                {notices.map((n) => {
                  const st = statusOf(n);
                  return (
                    <li key={n.id} className={`admin-nb-row is-${st}`}>
                      <div className="admin-nb-row-main">
                        <div className="admin-nb-row-top">
                          <span className={`admin-nb-chip is-${st}`}>{st}</span>
                          <span className="admin-nb-chip">{n.kind}</span>
                          <span className="admin-nb-muted">{n.date} · rev {n.revision} · {(n.surfaces || []).join(' + ') || '—'}</span>
                        </div>
                        <div className="admin-nb-heading">{n.heading}</div>
                        <div className="admin-nb-body">{n.body}</div>
                        <div className="admin-nb-muted">
                          {n.affects_from && n.affects_to ? `Affects ${short(n.affects_from)} to ${short(n.affects_to)} · ` : ''}
                          Published {short(n.publish_from)}{n.publish_to ? ` until ${short(n.publish_to)}` : ' until withdrawn'}
                          {n.updated_by ? ` · last changed by ${n.updated_by}` : ''}
                        </div>
                      </div>
                      {st !== 'withdrawn' && (
                        <div className="admin-nb-row-actions">
                          <button type="button" className="admin-surface-save" disabled={busy} onClick={() => { setError(''); setForm(formOf(n)); }}>Edit</button>
                          <button type="button" className="admin-surface-save" disabled={busy} onClick={() => withdraw(n)}>Withdraw</button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}

        {form && (
          <div className="admin-nb-form">
            <div className="admin-nb-grid">
              <label>Kind
                <select value={form.kind} onChange={(e) => set({ kind: e.target.value })}>
                  <option value="maintenance">Maintenance</option>
                  <option value="incident">Incident</option>
                  <option value="info">Update</option>
                </select>
              </label>
              <label>Date the notice is about
                <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} />
              </label>
            </div>

            <label>Heading <em>{form.heading.trim().length} / 80</em>
              <input type="text" value={form.heading} maxLength={80} onChange={(e) => set({ heading: e.target.value })} placeholder="System Maintenance" />
            </label>
            <label>Body <em>{form.body.length} / 600 · plain text, shown exactly as typed</em>
              <textarea rows={4} value={form.body} maxLength={600} onChange={(e) => set({ body: e.target.value })} placeholder="What is happening, and what readers will notice." />
            </label>

            <div className="admin-nb-grid">
              <label>Affects from <em>optional · {zone}</em>
                <input type="datetime-local" value={form.from} onChange={(e) => set({ from: e.target.value })} />
              </label>
              <label>Affects to
                <input type="datetime-local" value={form.to} onChange={(e) => set({ to: e.target.value })} />
              </label>
            </div>

            <fieldset>
              <legend>Shown on</legend>
              <label className="admin-nb-check"><input type="checkbox" checked={form.suite} onChange={(e) => set({ suite: e.target.checked })} /> Suite: riftworks.tech pages and the suite login</label>
              <label className="admin-nb-check"><input type="checkbox" checked={form.kuestion} onChange={(e) => set({ kuestion: e.target.checked })} /> Kuestion: product page, login, web app and Android</label>
            </fieldset>

            <div className="admin-nb-grid">
              <label>Publish from <em>blank = now</em>
                <input type="datetime-local" value={form.publishFrom} onChange={(e) => set({ publishFrom: e.target.value })} />
              </label>
              <label>Publish until <em>blank = {!form.id && form.kind === 'maintenance' && form.to ? 'window end + 24 h' : 'until withdrawn'}</em>
                <input type="datetime-local" value={form.publishTo} onChange={(e) => set({ publishTo: e.target.value })} />
              </label>
            </div>

            {form.id && (
              <label className="admin-nb-check admin-nb-material">
                <input type="checkbox" checked={form.material} onChange={(e) => set({ material: e.target.checked })} />
                <span><strong>This is a material change.</strong> The window moved, the scope changed, or the heading now says something different. Everyone who already read it sees it again as New. Leave unticked for a typo.</span>
              </label>
            )}

            <div className="admin-nb-bar">
              <button type="button" className="admin-nb-primary" disabled={Boolean(problem) || busy} onClick={save}>{busy ? 'Saving…' : form.id ? 'Save changes' : 'Publish notice'}</button>
              <button type="button" className="admin-surface-save" onClick={() => setForm(null)}>Cancel</button>
              {problem && <span className="admin-nb-muted">{problem}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
